import { Position } from '@capacitor/geolocation';
import ChunkedScene from '../../classes/ChunkedScene';
import { Player } from '../../classes/Player';
import { Creature } from 'src/classes/Creature';
import { LatLonToXYConverter } from 'src/classes/LatLonToXY';
import { PlacesList } from '../../classes/dataClasses/Places';
import { GameObjectManager } from 'src/classes/GameObjectManager';
import { DropItem } from 'src/classes/DropItem';
import { Landscape } from 'src/classes/Landscape';
import { LandmarkManager } from 'src/classes/Landmarks';
import UIScene from 'src/classes/UI/UIScene';
import VirtualJoyStick from 'phaser3-rex-plugins/plugins/virtualjoystick';
import { Preferences } from '@capacitor/preferences';
import { GpsModeManager } from 'src/classes/GpsMode';
import { WeatherService } from 'src/classes/WeatherService';
import { PlayerMetadata } from 'src/classes/PlayerMetadata';
import { WebsocketService } from '../services/websocket.service';
import { Memory } from 'src/classes/Memories';
import { Bullet } from 'src/classes/SlingShot';
import { GlobalService } from '../services/global.service';
import { FueFolletList } from 'src/classes/dataClasses/FueFolletMappings';
import { FueFollet } from 'src/classes/FueFollet';
import { bass, gar, name_to_tile_id, water } from 'src/classes/dataClasses/Mappings';
import { environment } from 'environment';
import { Peer } from 'src/classes/Peer';
import { MixTape } from 'src/classes/MixTape';
import { syncGameStateWithServer } from '../utils/game-state-manager';
import { UserAudio } from 'infrastructure/lib/src/models/user-audio';

export default class MainScene extends ChunkedScene {
  player: Player;
  other: Player;
  tiles: Phaser.Tilemaps.Tileset;
  playerPrevX: integer = 0;
  playerPrevY: integer = 0;
  text: Phaser.GameObjects.Text;
  positionText: Phaser.GameObjects.Text;
  orientationText: Phaser.GameObjects.Text;
  UI: UIScene;
  joystick: VirtualJoyStick;
  rotationCursor: Phaser.Types.Input.Keyboard.CursorKeys;
  camRotation: number = 0;
  activeCreatures: Creature[] = [];
  landmarkManager: LandmarkManager;
  initialLocation: Position;
  static gpsMode: GpsModeManager;
  public playerMetadata: PlayerMetadata = new PlayerMetadata('default');
  public static playerMetadata: PlayerMetadata = new PlayerMetadata('default');
  otherPlayers: Phaser.GameObjects.Group;
  public static uploadedImageNames: string[];
  fueFollets: Array<FueFollet> = [];
  static tokens: Array<string> = [];
  rt: Phaser.GameObjects.RenderTexture;
  lat: number;
  lon: number;
  public static music: Phaser.Sound.NoAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;
  previousTime = -1;
  public compassAngle: number = 0;
  playerScale: number = 1;
  static playerX: number = 0;
  static playerY: number = 0;
  // Cache for photos and audio
  static chunkPhotoCache: Map<number, any[]> = new Map();
  static chunkAudioCache: Map<number, UserAudio[]> = new Map();
  static currentInstance: MainScene;

  constructor(lat?: number, lon?: number) {
    super({ key: 'mainScene' });
    MainScene.currentInstance = this;
    LatLonToXYConverter.setScene(this);
    this.landmarkManager = new LandmarkManager();
    if(lat && lon){
      console.log(`Received: lat ${lat}, lon: ${lon}`)
    }
    this.lat = lat;
    this.lon = lon;
  }

  async preload() {
    // load the tileset image
    this.load.image('tiles', 'assets/sourceImages/terrain_atlas.png');

    // load the chunk master
    this.load.json('master', `assets/chunks/master.json`);

    // load the render texture mask to do lighting effects
    this.load.image('mask', 'assets/masks/mask1.png');

    // load small town theme music
    this.load.audio('theme', ['assets/music/GoneTubin.mp3']);

    // load music for Japanese Garden
    this.load.audio('Oriole', ['assets/music/Oriole.mp3']);

    // Include logic for GPS Mode (GPS, mag, accel)
    MainScene.gpsMode = new GpsModeManager(this);

    // Load the weather service resources
    WeatherService.preload(this);

    // Setup online multiplayer with socket.io
    WebsocketService.setScene(this);

    // Setup Memories
    Memory.preload(this);

    // Setup MixTapes
    MixTape.preload(this);

    // Load Jessie
    this.load.atlas(
      'Jessie',
      'assets/sprites/F_10.png',
      'assets/sprites/character_atlas.json'
    );
    this.load.animation('F_10_anims', 'assets/sprites/F_10_anim.json');
  }

  static async getTokens(){
    const retVal = (await Preferences.get({key: "tokens"})).value;
    MainScene.tokens = retVal ? JSON.parse(retVal) : [];
  }

  async create() {

    // We will be loading files on the fly, so we need to listen to events triggered when
    // a file (a tilemap, more specifically) is added to the cache
    this.cache.tilemap.events.on('add', async (cache, key) => {
      if (key.includes('chunk')) {
        let chunkId = await this.displayChunk(key);
        this.placeAllImagesOnChunk(chunkId);
        this.placeAllAudioOnChunk(chunkId);
      }
    });

    // Retrieve the players previous position
    const ret = await Preferences.get({ key: 'location' });
    try {
      this.initialLocation = JSON.parse(ret.value).location;
    } catch {
      console.log("No locations have previously been visited.");
    }

    // Add theme music
    MainScene.music = this.sound.add('theme');
    MainScene.music.setLoop(true);
    MainScene.music.play();

    GlobalService.$musicPlaying.subscribe((e) => {
      this.updateMusic();
    })

    // In order to support multi-touch we need a second pointer
    this.input.addPointer(1);

    // Setup chunked maps
    const masterData = this.cache.json.get('master');
    ChunkedScene.chunkWidth = masterData.chunkWidth;
    ChunkedScene.chunkHeight = masterData.chunkHeight;
    ChunkedScene.tileWidth = masterData.tileWidth;
    ChunkedScene.tileHeight = masterData.tileHeight;
    ChunkedScene.nbChunksHorizontal = masterData.nbChunksX;
    ChunkedScene.nbChunksVertical = masterData.nbChunksY;

    this.objectManager = new GameObjectManager(
      this,
      ChunkedScene.chunkWidth,
      ChunkedScene.tileWidth,
      ChunkedScene.tileHeight
    );

    // Add audio as static attributes to each class
    DropItem.sound = this.sound.add('pickup');
    Landscape.rockSound = this.sound.add('rock');
    Landscape.bushSound = this.sound.add('bush');
    Memory.sound = this.sound.add('pickup');
    MixTape.sound = this.sound.add('pickup');
    Bullet.create(this);
    Creature.create(this);

    // Add the UI Scene
    this.scene.launch('UIScene', { mainScene: this });


    // If a lat lon was provided in the constructor (via a url parameter)
    // start the game at that position
    if(this.lat && this.lon){
      var {x, y} = LatLonToXYConverter.latlngToScreenXY(this.lat, this.lon)
    }
    // Place player at the last visited location if it is in storage
    else if (this.initialLocation && this.initialLocation.coords) {
      var { x, y } = LatLonToXYConverter.latlngToScreenXY(
        this.initialLocation.coords.latitude,
        this.initialLocation.coords.longitude
      );
    } 
    // Otherwise place the player at the games starting point
    else {
      const home: Position = PlacesList.find((place) => place.name == 'Brandt Hall');
      var { x, y } = LatLonToXYConverter.latlngToScreenXY(
        home.coords.latitude,
        home.coords.longitude
      );
    }

    // Create a player
    this.player = new Player({
      scene: this,
      x: x + 5 * ChunkedScene.tileWidth,
      y: y + 5 * ChunkedScene.tileHeight,
      texture: 'player',
    });

    // Make camera follow player
    console.log("GlobalService.zoom:", GlobalService.zoom);
    this.cameras.main.setZoom(GlobalService.zoom);
    this.cameras.main.startFollow(this.player, true, 0.05, 0.05);

    // add a render texture for nighttime
    const hypotenuse = 2*Math.sqrt(window.innerWidth**2 + window.innerHeight**2)
    this.rt = this.add.renderTexture(0, 0, hypotenuse, hypotenuse);
    this.rt.setPosition(this.player.x, this.player.y);
    this.rt.setDepth(5);

    // Add a cursor for rotation with callbacks
    this.rotationCursor = this.input.keyboard.createCursorKeys();

    // Add listener for changes to playerXY and update player position
    GlobalService.$playerXY.subscribe({
      next: async coords => {
        if(coords && coords.x && coords.y){
          console.log(`Jumping to x:${coords.x}, y: ${coords.y}`);
          this.player.x = coords.x;
          this.player.y = coords.y;
        }
      }
    });

    // Add debug text for position information
    this.positionText = this.add
      .text(150, 100, '', {
        font: '16px Courier',
      })
      .setDepth(1);
    this.positionText.setScrollFactor(0);

      // Add debug text for orientation information
      this.orientationText = this.add
      .text(300, 400, '', {
        font: '18px Courier',
      })
      .setDepth(5);
    this.orientationText.setScrollFactor(0);

    // Add fue follet to show you the way
    FueFolletList.forEach((ff) => {
      if(!ff.hidden) {
        this.fueFollets.push(new FueFollet(this, ff));
      }
    })

    // Add landmarks
    this.landmarkManager.create(this);

    // Setup the weather simulations
    WeatherService.create(this);

    // Add other players for online
    this.otherPlayers = this.add.group();
  }

  static hotFix(){
      // For backwards compatibility, if the player has the token for 
      // VCU Engineering Building, but not the token for Electric Arc Seen,
      // remove the old token and insert "Spoke with Klein" at index 1.
      console.log("Running FueFollet hotfix...");
      let tokens = MainScene.tokens as Array<string> | null;

      if (!tokens) return;

      // Check if "Spoke with Klein" is missing but "VCU Engineering Building" is present
      if (!tokens.includes("Spoke with Klein") && tokens.includes("VCU Engineering Building")) {
          // Insert the new token at index 1
          const insertIndex = 1;
          tokens.splice(insertIndex, 0, "Spoke with Klein");

          // Move VCU Engineering Building to the end to maintain order.
          tokens = tokens.filter(token => token !== "VCU Engineering Building");
          tokens.push("VCU Engineering Building");
      }

      // Check if the they have the token "VCU Engineering Building" when they shouldn't
      if (tokens.includes("VCU Engineering Building") && !tokens.includes("Electric Arc Seen")) {
        // Remove any invalid occurrences of "VCU Engineering Building" token
        tokens = tokens.filter(token => token !== "VCU Engineering Building");
      }
      Preferences.set({key: "tokens", value: JSON.stringify(tokens)});
      MainScene.tokens = tokens;
  }

  async getImagesMetadataByChunkId(chunkId: number) {
    if (MainScene.chunkPhotoCache.has(chunkId)) {
      return [];
    }
    try {
      const response = await fetch(`${environment.REST_API}/photos?chunkId=${chunkId}`);
      const { photos } = await response.json();
      
      // Cache the photo data for this chunk
      MainScene.chunkPhotoCache.set(chunkId, photos);
      
      return photos;
    } catch (error) {
        console.error('Error loading chunk ' + chunkId + ':', error);
        return [];
    }
  }

    async getAudioMetadataByChunkId(chunkId: number) {
    if (MainScene.chunkAudioCache.has(chunkId)) {
      return [];
    }
    try {
      const response = await fetch(`${environment.REST_API}/audio?chunkId=${chunkId}`);
      // UserAudio
      const { audio } = (await response.json()) as { audio: UserAudio[] };

      // Cache the audio data for this chunk
      MainScene.chunkAudioCache.set(chunkId, audio);

      return audio;
    } catch (error) {
        console.error('Error loading chunk ' + chunkId + ' audio:', error);
        return [];
    }
  }

  clearPeers() {
    console.log("Clearing all peers from exited scene");
    if (this.otherPlayers && this.otherPlayers.getChildren()) {
      this.otherPlayers.getChildren().forEach((peer: Peer) => {
        peer.customDestroy();
        this.otherPlayers.remove(peer, true, true);
      });
    }
  }

  placeImage(s3Path: string, offset=0){
    const { x, y } = Memory.extractCoordsFromFilename(s3Path);
    if (x && y) {
      new Memory(this, x + offset, y + offset, s3Path);
    }
  }

  async placeAllImagesOnChunk(chunkId: number){
    const imagesMetadata = await this.getImagesMetadataByChunkId(chunkId);
    imagesMetadata.forEach((imageMetadata: any) => {
      this.placeImage(imageMetadata.s3Path);
    });
  }

  placeAudio(audioMetadata: UserAudio){
    const { x, y } = Memory.extractCoordsFromFilename(audioMetadata.s3Path);
    if (x && y) {
      new MixTape(this, x, y, audioMetadata);
    }
  }


  async placeAllAudioOnChunk(chunkId: number){
    const audioMetadata = await this.getAudioMetadataByChunkId(chunkId);
    audioMetadata.forEach((audioMetadata: UserAudio) => {
      this.placeAudio(audioMetadata);
    });
  }

  configureCameraForGPS() {
    let { width, height } = this.sys.game.canvas;
    let newCamSize = Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2));
    this.cameras.main.width = newCamSize;
    this.cameras.main.height = newCamSize;
    this.cameras.main.x = (width - newCamSize) / 2;
    this.cameras.main.y = (height - newCamSize) / 2;
  }

  configureCameraForJoystick() {
    let { width, height } = this.sys.game.canvas;
    this.cameras.main.width = width;
    this.cameras.main.height = height;
    this.cameras.main.x = 0;
    this.cameras.main.y = 0;
  }

  updateGeoPosText(position: Position) {
    //The heading attribute denotes the direction of travel
    // of the hosting device and is specified in degrees,
    // where 0° ≤ heading < 360°, counting clockwise relative to the true north.
    const posText: string[] = [
      `latitude: ${position.coords.latitude}`,
      `longitude: ${position.coords.longitude}`,
      `accuracy: ${position.coords.accuracy} (m)`,
      `heading (deg from north): ${position.coords.heading}`,
      `timestamp: ${position.timestamp}`,
    ];
    // this.positionText.setText(posText);
  }

  linkJoystickToPlayer() {
    if (!this.joystick) {
      this.joystick = (
        this.scene.manager.getScene('UIScene') as UIScene
      ).customJoystick.joystick;
      this.player.cursors =
        this.joystick.createCursorKeys() as Phaser.Types.Input.Keyboard.CursorKeys;
    } else {
      this.player.update(this.joystick);
    }
  }

  getTileUnderCoordinate(x: number, y: number){
    const chunkID = ChunkedScene.computeChunkID(x, y);
    if(this.maps[chunkID]){
      const baseTileValues = this.objectManager.getBaseTileValues(this.maps[chunkID]);
      const {col, row} = ChunkedScene.getNearestTileIndices(x, y);
      return baseTileValues[row * ChunkedScene.chunkWidth + col];
    }
    return null;
  }

  computeBrightness(hrs: number, min: number = 0.3){
    return min + (1-min)*(Math.sin((Math.PI)*(hrs - 6)/12) + 1)/2
  }

  updateSunlight(){
    const date = new Date();
    const hrs = date.getHours();
    const mins = date.getMinutes();
    const time = hrs + mins/60;

    const alpha = this.computeBrightness(time);

    //  Clear the RenderTexture
    this.rt.clear();

    //  Fill it in black
    this.rt.fill(0x0b0447, 1 - alpha);
    this.rt.setDepth(5);
    this.rt.setPosition(this.player.x, this.player.y);


    //  Erase the 'mask' texture from it based on the player position
    //  We - 107, because the mask image is 213px wide, so this puts it on the middle of the player
    this.rt.erase('mask', this.rt.width/2-107, this.rt.height/2-107);
  }

  updateMusic(){
    if(GlobalService.$musicPlaying.getValue() && MainScene.music.isPaused){
      MainScene.music.resume();
    }
    else if(!GlobalService.$musicPlaying.getValue() && !MainScene.music.isPaused){
      MainScene.music.pause();
    }
  }

  updatePlayerJump(){
    if(MainScene.gpsMode.isConnected && this.player.gpsX && this.player.gpsY){

      const fromX = this.player.x;
      const fromY = this.player.y;

      const toX = this.player.gpsX;
      const toY = this.player.gpsY;

      const distance = Math.sqrt((fromX - toX)**2 + (fromY - toY)**2);

      if(distance <= 3000){
        this.player.x = fromX + (toX - fromX)* 0.01;
        this.player.y = fromY + (toY - fromY)* 0.01;
      }
      else{
        this.player.x = toX;
        this.player.y = toY;
      }
    }
  }

  update(time: number, delta: number): void {

    WebsocketService.scene = this.scene.scene as MainScene;

    // every 16ms. Game logic here
    this.linkJoystickToPlayer();

    // Throttle Websocket to make less glitchy and lower cost
    if (
      (this.player.x - this.playerPrevX) ** 2 +
        (this.player.y - this.playerPrevY) ** 2 >
      200
    ) {
      this.playerPrevX = this.player.x;
      this.playerPrevY = this.player.y;
      this.updateEnvironment();
      if (this.playerMetadata.update(this.player)) {
        this.playerMetadata.levelName = this.scene.key;
        this.playerMetadata.lastUpdated = Date.now();
        MainScene.playerMetadata = this.playerMetadata;
        WebsocketService.updatePlayer(this.playerMetadata);
      }
    }
    // Update the global player position for use in other components
    MainScene.playerX = this.player.x;
    MainScene.playerY = this.player.y;

    this.activeCreatures = this.activeCreatures.filter((ac) => {
      return ac !== undefined;
    });

    // Determine if the player is on water or land
    // and update graphics and animations accordingly
    const tileIdUnderPlayer = this.getTileUnderCoordinate(this.player.x, this.player.y);
    this.player.onWater = tileIdUnderPlayer === name_to_tile_id.get(water)

    // Restrict animals from entering/exiting water
    const waterTileId = name_to_tile_id.get(water);
    this.activeCreatures.forEach((c) => {
      if (c && c !== undefined && c !== null && !c.dead) {
        if(c.previousPosition.length() > 0){
          const tileUnderCreature = this.getTileUnderCoordinate(c.x, c.y);
          if(tileUnderCreature !== null){
            const aquatic = c.name === bass || c.name === gar;
            c.onWater = tileUnderCreature === waterTileId;
            c.allowedOnTile = aquatic && c.onWater || !aquatic && !c.onWater;
            const prevRowCol = ChunkedScene.getNearestTileIndices(c.previousPosition.x, c.previousPosition.y);
            c.previousRow = prevRowCol.row;
            c.previousCol = prevRowCol.col;
            const currentRowCol = ChunkedScene.getNearestTileIndices(c.x, c.y);
            c.currentRow = currentRowCol.row;
            c.currentCol = currentRowCol.col;

          }
        }
        c.previousPosition = c.position;
        c.update();
      }
      else{
        c.destroy();
        c = null;
      }
    });

    // Smoothly jump player to new GPS location if it is a small enough jump
    this.updatePlayerJump();

    // Add memories to the main scene as they become available
    Memory.placeNewMemories(this);

    // Add sunlight and shade
    this.updateSunlight();

    // Move fue follet to guide user to next location
    this.fueFollets.forEach((ff) => {
      ff.update();
    });
  }
}
